// Square-pad a brand or org logo at upload time: the logo is flattened onto
// white and centred on a white square with a margin around it, so it never
// renders "trimmed" anywhere it is shown (dashboard preview, the cartera
// cards and signed-acta hero in the PWA). This is a purely client-side,
// cosmetic transform — the server presign / HEAD / objectKey checks remain
// the security boundary (canvas output is not trusted).
//
// White, not the logo's own edge colour: logos are usually trimmed to their
// artwork, so the "edge colour" was the logo itself and the padding looked
// like a smear of it (founder review of the Forus brands, 2026-10-20). The
// margin keeps edge-to-edge wordmarks from touching the frame.
//
// Why client-side (ADR-0033 stands, no new ADR): media-svc deliberately
// never decodes image bytes; doing the pad here keeps that invariant and
// adds zero native-image dependency to the backend. The uploaded logo is
// not embedded in the signed PDF and each upload lands at a fresh object
// key, so re-padding only ever affects future uploads — never a signed acta.
//
// Browser-only (uses createImageBitmap + <canvas>). Read contentType and
// contentLength from the returned blob — NOT the source File — because the
// presigned PUT signs both, and the padded blob differs in size and may
// differ in byte count from the original.

export type AllowedMime = "image/png" | "image/jpeg" | "image/webp";
export type PadResult = { blob: Blob; mimeType: AllowedMime };

// Re-encode quality per format. PNG is lossless from canvas (the quality
// arg is ignored); JPEG/WebP at 0.92 are visually lossless for logos.
const QUALITY: Record<AllowedMime, number | undefined> = {
  "image/png": undefined,
  "image/jpeg": 0.92,
  "image/webp": 0.92,
};

// Mirrors the --logo-pad-fill / --paper token.
const FILL = "#FFFFFF";

/** Share of the square's side left empty on each side of the logo. */
export const LOGO_MARGIN_RATIO = 0.1;
/** Longest side of the uploaded square; larger sources are scaled down. */
export const LOGO_MAX_SIDE = 1024;

export type SquareLayout = { size: number; dx: number; dy: number; dw: number; dh: number };

/**
 * Where a w×h logo lands on its square: the logo keeps its aspect ratio and
 * fits inside the square minus LOGO_MARGIN_RATIO on every side. The square
 * is as large as the source allows (no upscaling), capped at LOGO_MAX_SIDE.
 */
export function squareLayout(w: number, h: number): SquareLayout {
  const inner = 1 - 2 * LOGO_MARGIN_RATIO;
  const size = Math.min(LOGO_MAX_SIDE, Math.round(Math.max(w, h) / inner));
  const scale = (size * inner) / Math.max(w, h);
  const dw = Math.round(w * scale);
  const dh = Math.round(h * scale);
  return { size, dx: Math.floor((size - dw) / 2), dy: Math.floor((size - dh) / 2), dw, dh };
}

/**
 * Place a raster logo, flattened onto white, in the middle of a white square
 * with a margin around it. Never crops. The output MIME mirrors the source
 * (PNG/JPEG/WebP).
 */
export async function padToSquare(file: File): Promise<PadResult> {
  const mimeType: AllowedMime =
    file.type === "image/png" || file.type === "image/jpeg" || file.type === "image/webp"
      ? file.type
      : "image/png";

  const bitmap = await createImageBitmap(file);
  try {
    const { size, dx, dy, dw, dh } = squareLayout(bitmap.width, bitmap.height);

    const canvas = document.createElement("canvas");
    canvas.width = size;
    canvas.height = size;
    const ctx = canvas.getContext("2d");
    if (!ctx) throw new Error("Canvas 2D no disponible.");

    // Painting white first flattens any transparency in the logo too.
    ctx.fillStyle = FILL;
    ctx.fillRect(0, 0, size, size);
    ctx.imageSmoothingQuality = "high";
    ctx.drawImage(bitmap, dx, dy, dw, dh);

    const blob = await new Promise<Blob>((resolve, reject) => {
      canvas.toBlob(
        (b) => (b ? resolve(b) : reject(new Error("canvas.toBlob devolvió null."))),
        mimeType,
        QUALITY[mimeType]
      );
    });
    return { blob, mimeType };
  } finally {
    bitmap.close();
  }
}
